import {
    MAX_PAGE_SIZE_FOR_COMPARISON,
    STATISTICS,
} from '@client/app/shared-modules/statistics/statistics.constant';
import { contextTokens } from '@monsido/ng2/services/request-auxiliary/request-auxiliary.service';
import type { StatisticsLocation } from '@client/app/modules/statistics/interfaces/statistics-vistor.interface';
import type {
    SiteAnalyticsDataRequest,
    SiteAnalyticsDataResult,
    SiteAnalyticsLinkCell,
    SiteAnalyticsScaffoldConfig,
} from '@client/app/feature-modules/site-analytics/types/site-analytics-page-scaffold.type';
import type {
    SiteAnalyticsPageDefinition,
    SiteAnalyticsPageDefinitionDeps,
} from '@client/app/feature-modules/site-analytics/types/site-analytics-page-definition.type';
import {
    buildLocationColumnAggregates,
    getCurrentPage,
    getNameLabel,
    getPerPage,
    getTotal,
    normalizeVisitorsRows,
} from '@client/app/feature-modules/site-analytics/definitions/visitors-definition.utils';

const VISITORS_LOCATIONS_CONFIG: SiteAnalyticsScaffoldConfig = {
    pageHeaderKey: 'siteAnalytics.visitors.locations.header',
    pageSubheaderKey: 'siteAnalytics.visitors.locations.subheader',
    tableKey: 'site-analytics-visitors-locations',
    dataSource: 'visitorsLocations',
    exportCategory: 'visitors_locations',
    searchSupported: false,
    emptyMessageKey: 'siteAnalytics.visitors.table.emptyMessage',
    emptySearchMessageKey: 'siteAnalytics.visitors.table.emptySearchMessage',
    tableCaptionKey: 'siteAnalytics.visitors.locations.table.caption',
    exportButtonLabelKey: 'siteAnalytics.scaffold.export.buttonLabel',
    dateRangeLabelKey: 'siteAnalytics.scaffold.dateRange.label',
    compareToggleLabelKey: 'siteAnalytics.scaffold.compare.toggleLabel',
    compareDateRangeLabelKey: 'siteAnalytics.scaffold.compare.dateRangeLabel',
    defaultSortBy: '',
    defaultSortDir: 'desc',
    comparisonMaxPageSize: MAX_PAGE_SIZE_FOR_COMPARISON,
    comparisonEnabled: false,
    chart: {
        titleKey: 'siteAnalytics.visitors.locations.chart.title',
        primarySeriesLabelKey: 'siteAnalytics.scaffold.chart.series.currentVisits',
        comparisonSeriesLabelKey: 'siteAnalytics.scaffold.chart.series.comparisonVisits',
    },
    columns: [
        {
            id: 'name',
            headerKey: 'siteAnalytics.scaffold.table.column.name',
            displayType: 'link',
            width: 'xl',
        },
        {
            id: 'visits',
            headerKey: 'siteAnalytics.scaffold.table.column.visits',
            displayType: 'number',
            width: 'md',
        },
        {
            id: 'bounceRate',
            headerKey: 'siteAnalytics.scaffold.table.column.bounceRate',
            displayType: 'text',
            width: 'md',
        },
        {
            id: 'actionsPerVisit',
            headerKey: 'siteAnalytics.scaffold.table.column.actionsPerVisit',
            displayType: 'text',
            width: 'md',
        },
        {
            id: 'avgTime',
            headerKey: 'siteAnalytics.scaffold.table.column.averageTime',
            displayType: 'duration',
            width: 'md',
        },
    ],
};

const loadVisitorsLocationsData = async (
    request: SiteAnalyticsDataRequest,
    deps: SiteAnalyticsPageDefinitionDeps,
): Promise<SiteAnalyticsDataResult> => {
    const { statsRepoService, statisticsParamsService } = deps;

    const params = statisticsParamsService.buildListParams({
        limit: request.perPage,
        page: request.page,
        domainId: request.domainId,
        dateRange: request.dateRange,
        dateFormat: STATISTICS.DATE_FORMAT,
        search: request.search,
    });

    const locationsResponse = await statsRepoService.getLocations(params, contextTokens.NO_PARSE_SEARCH);
    const rows = normalizeVisitorsRows(locationsResponse, (location) => {
        return createLocationLinkCell(location as StatisticsLocation);
    });

    return {
        rows,
        chart: {
            labels: rows.map((row) => getNameLabel(row)),
            primaryValues: rows.map((row) => toNumber(row.visits)),
        },
        summary: null,
        columnAggregates: buildLocationColumnAggregates(locationsResponse),
        total: getTotal(locationsResponse, rows.length),
        perPage: getPerPage(locationsResponse, request.perPage),
        page: getCurrentPage(locationsResponse, request.page),
    };
};

function createLocationLinkCell (location: StatisticsLocation): SiteAnalyticsLinkCell | string {
    const countryCode = String(location.country_code || '');
    const countryName = String(location.label || countryCode || '');

    if (!countryCode) {
        return countryName;
    }

    return {
        type: 'state',
        label: countryName,
        state: 'base.customer.domain.statistics.visitors.locations.regions',
        params: {
            countryCode,
            countryName,
        },
    };
}

function toNumber (value: unknown): number {
    const parsed = Number.parseInt(String(value ?? 0), 10);
    return Number.isNaN(parsed) ? 0 : parsed;
}

export const VISITORS_LOCATIONS_DEFINITION: SiteAnalyticsPageDefinition = {
    key: 'visitorsLocations',
    config: VISITORS_LOCATIONS_CONFIG,
    loadData: loadVisitorsLocationsData,
};

export const VISITORS_LOCATIONS_PAGE_CONFIG = VISITORS_LOCATIONS_DEFINITION.config;
